import { unsafeCSS, html } from "lit";
import { property } from "lit/decorators.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { FormControlMixin } from "../../utilities/mixins/form-control.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { requiredValidator } from "../../utilities/validators.js";
import { LeafElement } from "../leaf-element.js";
import { LeafValidationMessage } from "../validation-message/validation-message.component.js";
const styles = `/*------------------------------------*\\
    #RESET
\\*------------------------------------*/
/**
 * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/
 */
*,
::slotted(*),
*:before,
*:after {
  box-sizing: border-box;
}

:host *,
:host *::before,
:host *::after {
  box-sizing: inherit;
}

[hidden] {
  display: none !important;
}

:host {
  --leaf-select-box-shadow-color: var(--leaf-color-border-active-outline);
  display: grid;
  position: relative;
}
:host .leaf-c-select__wrapper {
  display: flex;
  flex-direction: column;
  margin: auto;
  width: calc(100% - var(--leaf-spacing-8));
}
:host ::slotted([slot=select]) {
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);
          text-decoration: var(--leaf-typography-body-default-text-decoration);
  text-transform: var(--leaf-typography-body-default-text-transform);
  align-self: center;
  -webkit-appearance: none;
     -moz-appearance: none;
          appearance: none;
  background-color: var(--leaf-color-bg-default);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%232d2d2d' viewBox='0 0 24 24' aria-hidden='true' class='leaf-c-icon'%3E%3Cpath d='M16.59 8 12 12.58 7.41 8 6 9.41l6 6 6-6L16.59 8Z'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: var(--leaf-spacing-24);
  background-position: center right var(--leaf-spacing-16);
  border: var(--leaf-border-width-default) solid var(--leaf-color-border-strong);
  border-radius: var(--leaf-spacing-8);
  color: var(--leaf-color-border-strong);
  height: calc(var(--leaf-spacing-16) * 3.5);
  margin: var(--leaf-spacing-4) 0;
  padding: var(--leaf-spacing-16);
  padding-right: var(--leaf-spacing-80);
  width: 100%;
}
:host ::slotted([slot=select]:focus),
:host ::slotted([slot=select]:focus-visible),
:host ::slotted([slot=select]:active) {
  box-shadow: 0 0 0 calc(var(--leaf-border-width-button-default) * 1) var(--leaf-color-border-knockout), 0 0 0 calc(var(--leaf-border-width-button-default) * 2) var(--leaf-select-box-shadow-color);
  outline: 0;
}
:host ::slotted([slot=select][invalid]) {
  --leaf-select-box-shadow-color: var(--leaf-color-border-error);
  border-color: var(--leaf-color-border-error);
}
:host ::slotted([slot=select]:disabled) {
  background-color: var(--leaf-color-bg-disabled);
  border-color: var(--leaf-color-border-disabled);
  color: var(--leaf-color-content-disabled) !important;
  pointer-events: none;
}
:host ::slotted([slot=select][valid]) {
  color: var(--leaf-color-content-default);
}
:host ::slotted([slot=note]) {
  margin-top: var(--leaf-spacing-4);
}

:host([success-opt-in]) leaf-icon[name=check-circle] {
  --icon-height: var(--leaf-spacing-24);
  color: var(--leaf-color-content-success);
  position: absolute;
  right: var(--leaf-spacing-48);
  top: var(--leaf-spacing-16);
}
:host([success-opt-in]) ::slotted([slot=select]:valid) {
  --leaf-select-box-shadow-color: var(--leaf-color-border-success);
  border-color: var(--leaf-color-border-success);
}`;
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let createUniqueId = 0;
const _LeafSelect = class _LeafSelect extends e(FormControlMixin(LeafElement)) {
  constructor() {
    super(...arguments);
    this.internals = this.attachInternals();
    this.validation = new ValidationController(this, {
      validators: [requiredValidator],
      nativeTarget: () => this.select
    });
    this.items = [];
    this.successOptIn = false;
  }
  get select() {
    return this.querySelector("select") || [];
  }
  /**
   * Get all select option elements
   */
  get selectOptions() {
    return this.querySelectorAll("leaf-select-option");
  }
  get note() {
    return this.querySelector('[slot="note"]');
  }
  connectedCallback() {
    super.connectedCallback();
    this.insertAdjacentHTML("afterbegin", this.createSelect());
    this.addEventListener("leaf:select-option-change", this.updateSelectOptions);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("leaf:select-option-change", this.updateSelectOptions);
  }
  async firstUpdated(changedProperties) {
    var _a;
    super.firstUpdated(changedProperties);
    await this.updateComplete;
    (_a = this.select).id || (_a.id = `leaf-select-${createUniqueId++}`);
    if (this.note) {
      this.note.id = `${this.select.id}-note`;
      this.select.setAttribute("aria-describedby", this.note.id);
    }
    if (this.selectOptions.length) {
      this.selectOptions.forEach((item) => {
        if (item.selected === true) {
          this.selectedItem = item.value;
        }
      });
      this.value = this.value ? this.value : this.selectedItem ? this.selectedItem : this.selectOptions[0].value;
      this.validation.validate(this.value ? this.value : void 0);
      this.valid = this.validation.validity.valid;
    }
  }
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async updated(changedProperties) {
    await this.updateComplete;
    if (this.select) {
      for (const [key, _value] of changedProperties) {
        const property2 = String(key);
        if (typeof this[property2] !== "undefined") {
          if (this[property2]) {
            this.select.setAttribute(property2, String(this[property2]));
            if (property2 === "value") {
              this.select.value = String(this[property2]);
              if (this.select.selectedIndex === -1) {
                this.select.value = "";
              }
              this.validateSelect();
            }
          } else {
            this.select.removeAttribute(property2);
          }
        }
      }
      this.setFormControlElement(this.select);
    }
  }
  updateSelectOptions() {
    this.select.innerHTML = "";
    this.select.insertAdjacentHTML("afterbegin", `${this.renderSelectOptions()}`);
  }
  /**
   * Render the select options
   */
  renderSelectOptions() {
    return Array.from(this.selectOptions).map((item) => {
      return `<option
        value="${item.getAttribute("value")}"
        ${this.value === item.getAttribute("value") ? "selected" : item.hasAttribute("selected") ? "selected" : ""}
        ${item.hasAttribute("disabled") ? "disabled" : ""}
          >${item.textContent}
      </option>`;
    }).join("");
  }
  createSelect() {
    return `<select
      class="leaf-c-select__select"
      slot="select"
      ${this.id ? `id="${this.id}-leaf"` : ""}
      ${this.required ? "required" : ""}
      ${this.disabled ? "disabled" : ""}
      ${this.invalid ? `invalid="${this.invalid}"` : ""}
      ${this.valid ? `valid="${this.valid}"` : ""}
      ${this.name ? `name="${this.name}"` : ""}
      ${this.value ? `value="${this.value}"` : ""}
    >
      ${this.renderSelectOptions()}
    </select>`;
  }
  validateSelect() {
    this.value = this.select.options[this.select.selectedIndex] ? this.select.options[this.select.selectedIndex].value : "";
    this.validation.validate(this.value ? this.value : void 0);
    this.valid = this.validation.validity.valid;
    this.validation.removeValidationMessage();
    this.select.removeAttribute("invalid");
    this.select.toggleAttribute("valid", this.valid);
    this.requestUpdate();
  }
  render() {
    return html`
      <div @change=${this.validateSelect} class="leaf-c-select__wrapper">
        <slot name="select"></slot>
        <slot name="suffix">
          ${this.valid && this.successOptIn ? html`<leaf-icon set="system" name="check-circle"></leaf-icon>` : null}
        </slot>
        <slot name="note"></slot>
      </div>
      ${this.validation.render()}
    `;
  }
};
_LeafSelect.styles = unsafeCSS(styles);
_LeafSelect.elementDefinitions = {
  "leaf-validation-message": LeafValidationMessage
};
_LeafSelect.formAssociated = true;
let LeafSelect = _LeafSelect;
__decorateClass([
  property({ type: Array })
], LeafSelect.prototype, "items", 2);
__decorateClass([
  property()
], LeafSelect.prototype, "value", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafSelect.prototype, "invalid", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafSelect.prototype, "valid", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafSelect.prototype, "disabled", 2);
__decorateClass([
  property({ reflect: true })
], LeafSelect.prototype, "name", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafSelect.prototype, "required", 2);
__decorateClass([
  property({ reflect: true, type: Boolean, attribute: "success-opt-in" })
], LeafSelect.prototype, "successOptIn", 2);
export {
  LeafSelect
};
//# sourceMappingURL=select.component.js.map
